import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// COMPONENTS
import Button from '../../components/Button';
import CircleImage from '../../components/CircleImage';
import LoadingSpinner from '../../components/LoadingSpinner';
import SearchOrganisationMembers from '../../components/SearchOrganisationMembers';

// TYPES
import {
	OrganisationAdministratorCandidatesUserProfile,
	OrganisationAdministratorsAdministrator,
	OrganisationAddAdministratorCommand,
	OrganisationRemoveAdministratorCommand
} from '../../api/models';
import { closeModalType } from '../../components/GlobalModal';

interface AdminsModalTypes {
	name: string | undefined;
	id: string | undefined;
	closeModal?: closeModalType;
	getCompaniesDetailData: () => Promise<void>;
}

const AdminsModal: React.FC<AdminsModalTypes> = ({ name, id, closeModal, getCompaniesDetailData }) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Set up Data state
	const [adminsFormData, setAdminsFormData] = React.useState<OrganisationAdministratorsAdministrator[]>([]);

	const [newAdmins, setnewAdmins] = React.useState<OrganisationAdministratorsAdministrator[]>([]);

	const getAdminsData = async (reload: boolean = true) => {
		if (reload) {
			setIsLoading(true);
		}

		const response = await apibridge.getOrganisationAdministrators(id);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setAdminsFormData(response.result);
		}
	};

	React.useEffect(() => {
		getAdminsData();
	}, []);

	const submitHandler = async (values: any) => {
		const admins: any[] = [];

		newAdmins.map((admin: any) => admins.push(admin.id));

		if (newAdmins.length) {
			const data: OrganisationAddAdministratorCommand = { profileIds: admins, organisationId: id };

			const response = await apibridge.postOrganisationAddAdmin(data);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				if (closeModal) {
					closeModal();
				}

				getCompaniesDetailData();
			}
		} else {
			if (closeModal) {
				closeModal();
			}
		}
	};

	const removeHandler = async (itemId: string, index: number) => {
		if (index === 0 && newAdmins.length >= 1) {
			const admins: string[] = [];

			newAdmins.map((item) => admins.push(item.id || ''));

			if (newAdmins.length) {
				const data: OrganisationAddAdministratorCommand = { profileIds: admins, organisationId: id };

				const response = await apibridge.postOrganisationAddAdmin(data);

				if (response.isError) {
					setIsLoading(false);

					setFormErrorMessage(
						response.validationErrors.length
							? validationErrorsToString(response.validationErrors)
							: response.message || 'An error has occurred'
					);
				} else {
					setIsLoading(false);

					setnewAdmins([]);

					const removeData: OrganisationRemoveAdministratorCommand = { profileId: itemId, organisationId: id };

					const response = await apibridge.postOrganisationRemoveAdmin(removeData);

					if (response.isError) {
						setIsLoading(false);

						setFormErrorMessage(
							response.validationErrors.length
								? validationErrorsToString(response.validationErrors)
								: response.message || 'An error has occurred'
						);
					} else {
						setIsLoading(false);

						getAdminsData(false);
					}
				}
			}
		} else {
			if (newAdmins.filter((user) => user.id === itemId)[0]) {
				setnewAdmins(newAdmins.filter((user) => user.id !== itemId));

				setIsLoading(false);
			} else {
				const removeAdminData: OrganisationRemoveAdministratorCommand = { profileId: itemId, organisationId: id };

				const response = await apibridge.postOrganisationRemoveAdmin(removeAdminData);

				if (response.isError) {
					setIsLoading(false);

					setFormErrorMessage(
						response.validationErrors.length
							? validationErrorsToString(response.validationErrors)
							: response.message || 'An error has occurred'
					);
				} else {
					setIsLoading(false);

					getAdminsData(false);
				}
			}
		}
	};

	const addAdminHandler = async (item: OrganisationAdministratorCandidatesUserProfile) => {
		if (item) {
			if (newAdmins.find((admin) => admin.id === item.id)) {
				return;
			} else {
				setnewAdmins((prevState) => [
					...prevState,

					{ id: item.id, userId: item.userId, name: item.name, profileImage: item.profileImage }
				]);
			}
		}
	};

	return (
		<div className="general-form mtop-0">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<>
					{isLoading ? (
						<LoadingSpinner show={isLoading} />
					) : (
						<>
							<h3 className="admin-modal-title">{name}</h3>

							<section className="info-section">
								<div className="section-header bold">Current Leaders</div>

								<ul>
									{adminsFormData &&
										adminsFormData.length > 0 &&
										adminsFormData.concat(newAdmins).map((item, index) => {
											return (
												<li key={item.id} className="row-container">
													<div className="row detail-row">
														<div className="col-2 col-lg-1 img-container pdl-0">
															<CircleImage src={item.profileImage} />
														</div>

														<div className="col-8 info-container align-self-center">{item.name}</div>

														<div className="col-2 col-lg-3 link-group pdr-0">
															{adminsFormData.concat(newAdmins).length <= 1 ? null : (
																<Button
																	className="btn-text mtop-0 pdr-0"
																	onClick={() => removeHandler(item.id!, index)}
																>
																	Remove
																</Button>
															)}
														</div>
													</div>
												</li>
											);
										})}
								</ul>
							</section>

							<section className="people-search-wrapper">
								<h2 className="search-heading">Add a member</h2>

								<SearchOrganisationMembers
									onChange={(item: OrganisationAdministratorCandidatesUserProfile) => addAdminHandler(item)}
									placeholder="Begin typing a staff name..."
									id={id}
									isFullWidth={true}
								/>
							</section>

							<div className="form-button-group w-100">
								<Button onClick={submitHandler} className="btn-primary w-100">
									Save changes
								</Button>
							</div>
						</>
					)}
				</>
			)}
		</div>
	);
};

export default AdminsModal;
